/**
 * PushPreviewModal Component
 *
 * Modal that shows a diff preview before pushing a template to an agent.
 * Displays side-by-side comparison of current agent CLAUDE.md vs new template.
 *
 * @module components/claude-templates/PushPreviewModal
 */

import { useState, useEffect, memo, useMemo } from 'react';
import { Upload, Loader2, X, FileText, AlertCircle, Home, FolderCode } from 'lucide-react';

/**
 * Highlight Markdown syntax within a line
 * Returns JSX with syntax highlighting for headers, code, bold, links, etc.
 */
function highlightMarkdownLine(content, baseTextColor) {
  if (!content) return null;

  // Check for header lines
  const headerMatch = content.match(/^(#{1,6})\s+(.*)$/);
  if (headerMatch) {
    const level = headerMatch[1].length;
    const headerColors = {
      1: 'text-blue-300 font-bold',
      2: 'text-blue-400 font-semibold',
      3: 'text-blue-400',
      4: 'text-blue-500',
      5: 'text-blue-500',
      6: 'text-blue-500',
    };
    return (
      <span className={headerColors[level]}>
        <span className="text-gray-500">{headerMatch[1]} </span>
        {headerMatch[2]}
      </span>
    );
  }

  // Check for code block markers
  if (content.match(/^```/)) {
    const lang = content.slice(3);
    return (
      <span className="text-purple-400">
        ```<span className="text-purple-300">{lang}</span>
      </span>
    );
  }

  // Check for list items
  const listMatch = content.match(/^(\s*)([-*+]|\d+\.)\s+(.*)$/);
  if (listMatch) {
    return (
      <span className={baseTextColor}>
        {listMatch[1]}
        <span className="text-cyan-400">{listMatch[2]}</span> {highlightInlineMarkdown(listMatch[3], baseTextColor)}
      </span>
    );
  }

  // Check for blockquote
  if (content.startsWith('>')) {
    return (
      <span className="text-gray-400 italic">
        <span className="text-indigo-400">&gt;</span>
        {content.slice(1)}
      </span>
    );
  }

  // Inline markdown highlighting for regular lines
  return highlightInlineMarkdown(content, baseTextColor);
}

/**
 * Highlight inline markdown elements (bold, italic, code, links)
 */
function highlightInlineMarkdown(text, baseTextColor) {
  if (!text) return null;

  // Simple regex-based highlighting for inline elements
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Try to match inline code first (backticks)
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      parts.push(
        <span key={key++} className="px-1 py-0.5 bg-gray-700 text-pink-400 rounded text-xs">
          {codeMatch[1]}
        </span>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Try to match bold (**text**)
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      parts.push(
        <span key={key++} className="font-bold text-white">
          {boldMatch[1]}
        </span>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Try to match italic (*text* or _text_)
    const italicMatch = remaining.match(/^[*_]([^*_]+)[*_]/);
    if (italicMatch) {
      parts.push(
        <span key={key++} className="italic text-gray-200">
          {italicMatch[1]}
        </span>
      );
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // Try to match links [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      parts.push(
        <span key={key++}>
          <span className="text-gray-500">[</span>
          <span className="text-indigo-400 underline">{linkMatch[1]}</span>
          <span className="text-gray-500">](</span>
          <span className="text-gray-600 text-xs">{linkMatch[2]}</span>
          <span className="text-gray-500">)</span>
        </span>
      );
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // No match - take one character
    parts.push(
      <span key={key++} className={baseTextColor}>
        {remaining[0]}
      </span>
    );
    remaining = remaining.slice(1);
  }

  return parts.length > 0 ? <>{parts}</> : <span className={baseTextColor}>{text}</span>;
}

const API_BASE = '/api';

// Location labels for UI display
const LOCATION_LABELS = {
  global: { label: 'Global', icon: Home, path: '~/.claude/CLAUDE.md' },
  project: { label: 'Project', icon: FolderCode, path: 'WORKING_DIR/.claude/CLAUDE.md' }
};

/**
 * Fetch diff preview from backend
 */
async function fetchDiffPreview(templateId, agentId, location = 'global') {
  const res = await fetch(`${API_BASE}/claude-templates/${templateId}/diff/${agentId}?location=${location}`, {
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to fetch diff preview');
  }
  return res.json();
}

/**
 * Parse unified diff into line-by-line changes
 */
function parseDiff(diffText) {
  if (!diffText) return [];

  const lines = diffText.split('\n');
  const parsed = [];
  let leftLineNum = 0;
  let rightLineNum = 0;

  for (const line of lines) {
    // Skip diff headers
    if (line.startsWith('---') || line.startsWith('+++') || line.startsWith('@@')) {
      // Parse hunk header for line numbers
      const match = line.match(/@@ -(\d+)/);
      if (match) {
        leftLineNum = parseInt(match[1], 10) - 1;
        rightLineNum = parseInt(match[1], 10) - 1;
      }
      continue;
    }

    if (line.startsWith('-')) {
      leftLineNum++;
      parsed.push({
        type: 'removed',
        content: line.slice(1),
        leftNum: leftLineNum,
        rightNum: null
      });
    } else if (line.startsWith('+')) {
      rightLineNum++;
      parsed.push({
        type: 'added',
        content: line.slice(1),
        leftNum: null,
        rightNum: rightLineNum
      });
    } else if (line.startsWith(' ') || line === '') {
      leftLineNum++;
      rightLineNum++;
      parsed.push({
        type: 'unchanged',
        content: line.startsWith(' ') ? line.slice(1) : line,
        leftNum: leftLineNum,
        rightNum: rightLineNum
      });
    }
  }

  return parsed;
}

/**
 * DiffLine component for rendering a single diff line with Markdown syntax highlighting
 */
const DiffLine = memo(function DiffLine({ line, side }) {
  const bgColor = {
    added: 'bg-green-900/30',
    removed: 'bg-red-900/30',
    unchanged: ''
  }[line.type];

  const textColor = {
    added: 'text-green-300',
    removed: 'text-red-300',
    unchanged: 'text-gray-300'
  }[line.type];

  const lineNum = side === 'left' ? line.leftNum : line.rightNum;
  const showContent = side === 'left'
    ? line.type !== 'added'
    : line.type !== 'removed';

  return (
    <div className={`flex ${bgColor} border-b border-gray-800`}>
      <span className="w-10 px-2 text-right text-xs text-gray-500 bg-gray-800/50 select-none flex-shrink-0">
        {lineNum || ''}
      </span>
      <span className={`flex-1 px-2 text-sm font-mono whitespace-pre-wrap break-words overflow-hidden`}>
        {showContent ? highlightMarkdownLine(line.content, textColor) : ''}
      </span>
    </div>
  );
});

/**
 * SideBySideDiff component with synchronized scrolling
 */
const SideBySideDiff = memo(function SideBySideDiff({ parsedDiff }) {
  if (!parsedDiff || parsedDiff.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No changes detected</p>
        </div>
      </div>
    );
  }

  // Synchronized scrolling - both panels scroll together
  return (
    <div className="flex flex-col h-full">
      {/* Headers - fixed at top */}
      <div className="flex flex-shrink-0 border-b border-gray-700">
        <div className="w-1/2 px-3 py-2 bg-red-900/20 border-r border-gray-700 text-sm font-medium text-red-300">
          Current (Agent)
        </div>
        <div className="w-1/2 px-3 py-2 bg-green-900/20 text-sm font-medium text-green-300">
          New (Template)
        </div>
      </div>

      {/* Synchronized scrolling content - one scrollbar for both */}
      <div className="flex-1 overflow-auto">
        {parsedDiff.map((line, idx) => (
          <div key={idx} className="flex">
            {/* Left side */}
            <div className="w-1/2 border-r border-gray-700">
              <DiffLine line={line} side="left" />
            </div>
            {/* Right side */}
            <div className="w-1/2">
              <DiffLine line={line} side="right" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

/**
 * UnifiedDiff component (alternative view) with Markdown syntax highlighting
 */
const UnifiedDiff = memo(function UnifiedDiff({ parsedDiff }) {
  if (!parsedDiff || parsedDiff.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No changes detected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      {parsedDiff.map((line, idx) => {
        const bgColor = {
          added: 'bg-green-900/30',
          removed: 'bg-red-900/30',
          unchanged: ''
        }[line.type];

        const textColor = {
          added: 'text-green-300',
          removed: 'text-red-300',
          unchanged: 'text-gray-300'
        }[line.type];

        const prefix = {
          added: '+',
          removed: '-',
          unchanged: ' '
        }[line.type];

        return (
          <div key={idx} className={`flex ${bgColor} border-b border-gray-800`}>
            <span className="w-10 px-2 text-right text-xs text-gray-500 bg-gray-800/50 select-none flex-shrink-0">
              {line.leftNum || ''}
            </span>
            <span className="w-10 px-2 text-right text-xs text-gray-500 bg-gray-800/50 select-none flex-shrink-0">
              {line.rightNum || ''}
            </span>
            <span className={`w-4 text-center text-sm ${textColor} select-none flex-shrink-0`}>
              {prefix}
            </span>
            <span className={`flex-1 px-2 text-sm font-mono whitespace-pre-wrap break-words overflow-hidden`}>
              {highlightMarkdownLine(line.content, textColor)}
            </span>
          </div>
        );
      })}
    </div>
  );
});

/**
 * PushPreviewModal Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {object} props.template - Template being pushed
 * @param {object} props.agent - Target agent
 * @param {string} [props.location='global'] - Target location ('global' or 'project')
 * @param {function} props.onConfirm - Confirm push callback
 * @param {function} props.onCancel - Cancel callback
 * @param {boolean} [props.isPushing=false] - Whether push is in progress
 */
const PushPreviewModal = memo(function PushPreviewModal({
  isOpen,
  template,
  agent,
  location = 'global',
  onConfirm,
  onCancel,
  isPushing = false
}) {
  const [diffData, setDiffData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side' | 'unified'

  // Fetch diff when modal opens
  useEffect(() => {
    if (isOpen && template?.id && agent?.id) {
      setIsLoading(true);
      setError(null);
      fetchDiffPreview(template.id, agent.id, location)
        .then(result => {
          setDiffData(result.data);
        })
        .catch(err => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setDiffData(null);
      setError(null);
    }
  }, [isOpen, template?.id, agent?.id, location]);

  // Parse diff for display
  const parsedDiff = useMemo(() => {
    if (!diffData?.diff) return [];
    return parseDiff(diffData.diff);
  }, [diffData?.diff]);

  // Calculate change stats
  const stats = useMemo(() => {
    if (!parsedDiff.length) return { added: 0, removed: 0, unchanged: 0 };
    return parsedDiff.reduce((acc, line) => {
      acc[line.type] = (acc[line.type] || 0) + 1;
      return acc;
    }, { added: 0, removed: 0, unchanged: 0 });
  }, [parsedDiff]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-6xl mx-4 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Push Preview</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPushing}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info Bar */}
        <div className="px-6 py-3 bg-gray-800/50 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-400">
              Template: <span className="text-white font-medium">{template?.name}</span>
            </span>
            <span className="text-gray-600">→</span>
            <span className="text-gray-400">
              Agent: <span className="text-white font-medium">{agent?.displayName || agent?.name}</span>
            </span>
            <span className="text-gray-600">@</span>
            <span className="flex items-center gap-1.5 text-gray-400">
              {(() => {
                const LocationIcon = LOCATION_LABELS[location]?.icon || Home;
                return <LocationIcon className="w-4 h-4" />;
              })()}
              <span className="text-indigo-400 font-medium">{LOCATION_LABELS[location]?.label || location}</span>
              <span className="text-gray-500 text-xs">({LOCATION_LABELS[location]?.path})</span>
            </span>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMode('side-by-side')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === 'side-by-side'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Side by Side
            </button>
            <button
              type="button"
              onClick={() => setViewMode('unified')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === 'unified'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Unified
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        {!isLoading && !error && parsedDiff.length > 0 && (
          <div className="px-6 py-2 border-b border-gray-700 flex items-center gap-4 text-xs flex-shrink-0">
            <span className="text-green-400">+{stats.added} added</span>
            <span className="text-red-400">-{stats.removed} removed</span>
            <span className="text-gray-500">{stats.unchanged} unchanged</span>
          </div>
        )}

        {/* Content - use h-[65vh] for explicit scrollable height */}
        <div className="h-[65vh] overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          ) : diffData?.isNewFile ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <p className="text-green-300 font-medium">New file will be created</p>
                <p className="text-gray-500 text-sm mt-1">No existing CLAUDE.md on agent</p>
              </div>
            </div>
          ) : viewMode === 'side-by-side' ? (
            <SideBySideDiff parsedDiff={parsedDiff} />
          ) : (
            <UnifiedDiff parsedDiff={parsedDiff} />
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-500">
            {diffData?.isNewFile
              ? `This will create a new ${LOCATION_LABELS[location]?.label || ''} CLAUDE.md file at ${LOCATION_LABELS[location]?.path}.`
              : `This will overwrite the existing ${LOCATION_LABELS[location]?.label || ''} CLAUDE.md at ${LOCATION_LABELS[location]?.path}.`}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPushing}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isPushing || isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-50"
            >
              {isPushing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Push to Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PushPreviewModal;

export { SideBySideDiff, UnifiedDiff, parseDiff };
